import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MagicMomentDayOverride,
  MentorProfileOverride,
  MentorStoriesOverride,
  NewMentorUiOverride,
  UserRecordsV2Override,
  useDevOverridesStore,
} from "@/lib/hooks/admin/use-dev-overrides";
import { isD7TriggerQaScenario } from "@/lib/hooks/admin/d7-trigger-qa-scenario";
import { D7_TRIGGER_QA_OPTIONS } from "@/components/mentor-workflows/d7-trigger-qa";
import { DemoAccountsSettings } from "@/components/settings/demo-accounts-settings";

const OPTIONS: { value: "off" | "day1" | "day2"; label: string }[] = [
  { value: "off", label: "Off" },
  { value: "day1", label: "Force Day 1 banner" },
  { value: "day2", label: "Force Day 2 banner" },
];

const NEW_MENTOR_UI_OPTIONS: { value: NewMentorUiOverride; label: string }[] = [
  { value: "default", label: "Default (follow feature flag)" },
  { value: "new", label: "Force new mentor UI" },
  { value: "old", label: "Force old conversations UI" },
];

const MENTOR_PROFILE_OPTIONS: {
  value: MentorProfileOverride;
  label: string;
}[] = [
  { value: "default", label: "Default (follow feature flag)" },
  { value: "on", label: "Force matching profile on" },
  { value: "off", label: "Force matching profile off" },
];

const MENTOR_STORIES_OPTIONS: {
  value: MentorStoriesOverride;
  label: string;
}[] = [
  { value: "default", label: "Default (follow feature flag)" },
  { value: "on", label: "Force mentor stories on" },
  { value: "off", label: "Force mentor stories off" },
];

function HidePhiPiiSetting() {
  const hidePhiPii = useDevOverridesStore((s) => s.hidePhiPii);
  const setHidePhiPii = useDevOverridesStore((s) => s.setHidePhiPii);
  const queryClient = useQueryClient();

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-black">PHI/PII</h2>
        <p className="text-sm text-stone-600">
          Hide mentee PHI/PII in this browser for demos. Persists in
          localStorage. Only visible to superadmins.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="hide-phi-pii"
          checked={hidePhiPii === true}
          onCheckedChange={(value) => {
            setHidePhiPii(value === true);
            void queryClient.cancelQueries();
            void queryClient.invalidateQueries();
          }}
        />
        <Label
          htmlFor="hide-phi-pii"
          className="cursor-pointer text-sm font-normal"
        >
          Hide PHI/PII
        </Label>
      </div>
    </section>
  );
}

const USER_RECORDS_V2_OPTIONS: {
  value: UserRecordsV2Override;
  label: string;
}[] = [
  { value: "default", label: "Default (new profile)" },
  { value: "on", label: "Force User Records v2 on" },
  { value: "off", label: "Force User Records v2 off" },
];

export function DevSettings({ phiOnly = false }: { phiOnly?: boolean }) {
  const magicMomentDay = useDevOverridesStore((s) => s.magicMomentDay);
  const forceDay2Modal = useDevOverridesStore((s) => s.forceDay2Modal);
  const forceAtRiskMomentum = useDevOverridesStore(
    (s) => s.forceAtRiskMomentum,
  );
  const setMagicMomentDay = useDevOverridesStore((s) => s.setMagicMomentDay);
  const setForceDay2Modal = useDevOverridesStore((s) => s.setForceDay2Modal);
  const setForceAtRiskMomentum = useDevOverridesStore(
    (s) => s.setForceAtRiskMomentum,
  );
  const newMentorUiOverride = useDevOverridesStore(
    (s) => s.newMentorUiOverride,
  );
  const setNewMentorUiOverride = useDevOverridesStore(
    (s) => s.setNewMentorUiOverride,
  );
  const mentorProfileOverride = useDevOverridesStore(
    (s) => s.mentorProfileOverride,
  );
  const setMentorProfileOverride = useDevOverridesStore(
    (s) => s.setMentorProfileOverride,
  );
  const mentorStoriesOverride = useDevOverridesStore(
    (s) => s.mentorStoriesOverride,
  );
  const setMentorStoriesOverride = useDevOverridesStore(
    (s) => s.setMentorStoriesOverride,
  );
  const mentorProfileMockData = useDevOverridesStore(
    (s) => s.mentorProfileMockData,
  );
  const setMentorProfileMockData = useDevOverridesStore(
    (s) => s.setMentorProfileMockData,
  );
  const userRecordsV2Override = useDevOverridesStore(
    (s) => s.userRecordsV2Override,
  );
  const setUserRecordsV2Override = useDevOverridesStore(
    (s) => s.setUserRecordsV2Override,
  );
  const d7TriggerQaScenario = useDevOverridesStore(
    (s) => s.d7TriggerQaScenario,
  );
  const setD7TriggerQaScenario = useDevOverridesStore(
    (s) => s.setD7TriggerQaScenario,
  );
  const reset = useDevOverridesStore((s) => s.reset);
  const queryClient = useQueryClient();

  const radioValue = magicMomentDay ?? "off";
  const selectedD7Trigger = D7_TRIGGER_QA_OPTIONS.find(
    (option) => option.value === d7TriggerQaScenario,
  );

  const handleRadioChange = (value: string) => {
    const next: MagicMomentDayOverride =
      value === "day1" || value === "day2" ? value : null;
    setMagicMomentDay(next);
  };

  if (phiOnly) {
    return (
      <div className="space-y-8 py-6">
        <HidePhiPiiSetting />
      </div>
    );
  }

  return (
    <div className="space-y-8 py-6">
      <HidePhiPiiSetting />
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-black">D7-12 trigger QA</h2>
          <p className="text-sm text-stone-600">
            Force one trigger across Notice, Attention, and Action for every
            mentorship. Persists in localStorage and is available only outside
            production.
          </p>
        </div>

        <Select
          value={d7TriggerQaScenario ?? "off"}
          onValueChange={(value) => {
            if (value === "off") {
              setD7TriggerQaScenario(null);
            } else if (isD7TriggerQaScenario(value)) {
              setD7TriggerQaScenario(value);
            }
          }}
        >
          <SelectTrigger className="w-full max-w-md">
            <SelectValue placeholder="Use live trigger logic" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="off">Off — use live trigger logic</SelectItem>
            {D7_TRIGGER_QA_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedD7Trigger ? (
          <p className="text-xs text-stone-500">
            Expected: {selectedD7Trigger.expected}
          </p>
        ) : null}

        {d7TriggerQaScenario === "activity_completed" ||
        d7TriggerQaScenario === "hub_content_shared" ? (
          <p className="text-xs text-amber-700">
            “Check it out” shows for visual QA. Without a real pending entity it
            displays a forced-UI alert instead of navigating.
          </p>
        ) : null}
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-black">Magic moment</h2>
          <p className="text-sm text-stone-600">
            Override the Day 1 / Day 2 magic-moment gating for any conversation
            you open. Persists in localStorage. Only visible to superadmins.
          </p>
        </div>

        <RadioGroup
          value={radioValue}
          onValueChange={handleRadioChange}
          className="gap-3"
        >
          {OPTIONS.map((opt) => (
            <div key={opt.value} className="flex items-center gap-2">
              <RadioGroupItem
                value={opt.value}
                id={`magic-moment-${opt.value}`}
              />
              <Label
                htmlFor={`magic-moment-${opt.value}`}
                className="cursor-pointer text-sm font-normal"
              >
                {opt.label}
              </Label>
            </div>
          ))}
        </RadioGroup>

        {magicMomentDay === "day2" && (
          <div className="flex items-center gap-2 pl-6">
            <Checkbox
              id="force-day2-modal"
              checked={forceDay2Modal}
              onCheckedChange={(value) => setForceDay2Modal(value === true)}
            />
            <Label
              htmlFor="force-day2-modal"
              className="cursor-pointer text-sm font-normal"
            >
              Also show the Day 2 modal
            </Label>
          </div>
        )}

        <p className="text-xs text-stone-500">
          Open any active conversation to see the banner.
        </p>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-black">Momentum alert</h2>
          <p className="text-sm text-stone-600">
            Force the at-risk momentum banner and chat-list badge for any
            conversation you open. Persists in localStorage. Only visible to
            superadmins.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="force-at-risk-momentum"
            checked={forceAtRiskMomentum}
            onCheckedChange={(value) => setForceAtRiskMomentum(value === true)}
          />
          <Label
            htmlFor="force-at-risk-momentum"
            className="cursor-pointer text-sm font-normal"
          >
            Force Momentum alert
          </Label>
        </div>

        <p className="text-xs text-stone-500">
          Open any active teen conversation to see the persistent banner.
        </p>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-black">Mentor chat UI</h2>
          <p className="text-sm text-stone-600">
            Override the new-vs-old mentor chat UI, bypassing the{" "}
            <code>new-mentor-ui</code> feature flag. Persists in localStorage.
            Non-production only.
          </p>
        </div>

        <RadioGroup
          value={newMentorUiOverride}
          onValueChange={(value) =>
            setNewMentorUiOverride(value as NewMentorUiOverride)
          }
          className="gap-3"
        >
          {NEW_MENTOR_UI_OPTIONS.map((opt) => (
            <div key={opt.value} className="flex items-center gap-2">
              <RadioGroupItem
                value={opt.value}
                id={`new-mentor-ui-${opt.value}`}
              />
              <Label
                htmlFor={`new-mentor-ui-${opt.value}`}
                className="cursor-pointer text-sm font-normal"
              >
                {opt.label}
              </Label>
            </div>
          ))}
        </RadioGroup>

        <p className="text-xs text-stone-500">
          Open the Chat tab to see the selected UI.
        </p>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-black">Matching profile</h2>
          <p className="text-sm text-stone-600">
            Override the mentor matching-profile flow, bypassing the{" "}
            <code>mentor-profile</code> feature flag. Persists in localStorage.
            Non-production only.
          </p>
        </div>

        <RadioGroup
          value={mentorProfileOverride}
          onValueChange={(value) =>
            setMentorProfileOverride(value as MentorProfileOverride)
          }
          className="gap-3"
        >
          {MENTOR_PROFILE_OPTIONS.map((opt) => (
            <div key={opt.value} className="flex items-center gap-2">
              <RadioGroupItem
                value={opt.value}
                id={`mentor-profile-${opt.value}`}
              />
              <Label
                htmlFor={`mentor-profile-${opt.value}`}
                className="cursor-pointer text-sm font-normal"
              >
                {opt.label}
              </Label>
            </div>
          ))}
        </RadioGroup>

        <div className="flex items-center gap-2">
          <Checkbox
            id="mentor-profile-mock-data"
            checked={mentorProfileMockData}
            onCheckedChange={(value) =>
              setMentorProfileMockData(value === true)
            }
          />
          <Label
            htmlFor="mentor-profile-mock-data"
            className="cursor-pointer text-sm font-normal"
          >
            Use mock data (no backend)
          </Label>
        </div>

        <p className="text-xs text-stone-500">
          Visit <code>/mentor-profile</code> to see the flow. Enable mock data to
          run the wizard end-to-end before the API exists.
        </p>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-black">Mentor stories</h2>
          <p className="text-sm text-stone-600">
            Override the new story editor, bypassing the{" "}
            <code>enable-mentor-stories</code> feature flag. Off keeps the
            legacy mentor stories tab. Persists in localStorage. Non-production
            only.
          </p>
        </div>

        <RadioGroup
          value={mentorStoriesOverride ?? "default"}
          onValueChange={(value) =>
            setMentorStoriesOverride(value as MentorStoriesOverride)
          }
          className="gap-3"
        >
          {MENTOR_STORIES_OPTIONS.map((opt) => (
            <div key={opt.value} className="flex items-center gap-2">
              <RadioGroupItem
                value={opt.value}
                id={`mentor-stories-${opt.value}`}
              />
              <Label
                htmlFor={`mentor-stories-${opt.value}`}
                className="cursor-pointer text-sm font-normal"
              >
                {opt.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-black">User Records v2</h2>
          <p className="text-sm text-stone-600">
            The new profile is the default. Force off returns to the old profile.
            Persists in localStorage.
          </p>
        </div>

        <RadioGroup
          value={userRecordsV2Override}
          onValueChange={(value) =>
            setUserRecordsV2Override(value as UserRecordsV2Override)
          }
          className="gap-3"
        >
          {USER_RECORDS_V2_OPTIONS.map((opt) => (
            <div key={opt.value} className="flex items-center gap-2">
              <RadioGroupItem
                value={opt.value}
                id={`user-records-v2-${opt.value}`}
              />
              <Label
                htmlFor={`user-records-v2-${opt.value}`}
                className="cursor-pointer text-sm font-normal"
              >
                {opt.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </section>

      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const wasHidden =
              useDevOverridesStore.getState().hidePhiPii === true;
            reset();
            if (wasHidden) {
              void queryClient.cancelQueries();
              void queryClient.invalidateQueries();
            }
          }}
        >
          Reset overrides
        </Button>
      </div>

      <DemoAccountsSettings />
    </div>
  );
}
